// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/runningTurn.test.ts
import {
  ASK_PUNTT_STREAM_MESSAGE_PREFIX,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import { type AskV2ThreadMessage } from "./types";

/**
 * The `w` warhol puts in front of a snapshot's message id.
 *
 * One declaration, in the package both workspaces already import: warhol writes
 * the item under this address, christo reads it back, and this side subscribes
 * to it, so a literal drifting on one of them is a socket that never receives a
 * frame. See `ASK_PUNTT_STREAM_MESSAGE_PREFIX` in `@mg/schemas`.
 */
export const STREAM_MESSAGE_PREFIX = ASK_PUNTT_STREAM_MESSAGE_PREFIX;

/**
 * What a message id would have to look like to be a stream snapshot's.
 *
 * DERIVED from the prefix, never re-spelled: the address and the shape it is
 * recognised by have to be one fact. A literal `w` here would mean changing
 * `ASK_PUNTT_STREAM_MESSAGE_PREFIX` builds `x<userMessageId>` while this still
 * matched `w...`, so the overlay's own row would stop reading as synthesized
 * and `runningTurnUserMessageId` would walk up from it again — the follow-up
 * bug, back through the front door. The id may carry no colon either: the
 * socket subscription key is `chatId:messageId`.
 */
const STREAM_MESSAGE_ID = new RegExp(
  `^${escapeRegExp(STREAM_MESSAGE_PREFIX)}[^:]+$`,
);

/** A literal, ready for a regex. The prefix is a scheme letter, not a pattern. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Whether an id is a snapshot address rather than a stored message id.
 *
 * A predicate, not a boolean: its caller has a `string | null` and goes on to
 * use the id as a string.
 */
export function isStreamSnapshotMessageId(
  messageId: string | null | undefined,
): messageId is string {
  return typeof messageId === "string" && STREAM_MESSAGE_ID.test(messageId);
}

/**
 * The message a stream address belongs to, or null when it is not one.
 *
 * The address IS the turn's message with a scheme letter in front
 * (`streamMessageIdFor`), so this is a read of the same fact rather than a
 * second derivation -- and it is the only way a consumer that holds no thread
 * can ask "which turn is this socket for?".
 */
export function turnUserIdFromStreamMessageId(
  messageId: string | null | undefined,
): string | null {
  return isStreamSnapshotMessageId(messageId)
    ? messageId.slice(STREAM_MESSAGE_PREFIX.length)
    : null;
}

/**
 * The row `applyStreamSnapshot` appends for a turn warhol has published but
 * christo has not written a row for.
 *
 * It is `pending` exactly like christo's placeholder, and it has to be told
 * apart from it: the overlay keys it by the snapshot ADDRESS (`w<userMessageId>`,
 * which is what makes it match the frame it carries), while christo's row is a
 * stored message with a stored id. A Mongo ObjectId is hex, so it can never
 * start with a `w` and the two can never be confused.
 *
 * Why it matters — the list the rail reads is the OVERLAID one, and with a
 * follow-up queued behind a running turn that list is
 * `[userA, placeholderA, userB, synthesized]`. Taking the newest `pending` row
 * as the marker would walk up from the SYNTHESIZED one and resolve `userB`,
 * the follow-up, whose turn has not started — the exact failure this module
 * exists to prevent. It also starves `runningTurnReviewId`, which then never
 * reaches `placeholderA` and leaves every live row unopenable.
 */
export function isSynthesizedRow(message: AskPunttMessage): boolean {
  return message.role === "assistant" && isStreamSnapshotMessageId(message._id);
}

/** Whether a row is christo's placeholder for a turn that is still running. */
function isPendingRow(message: AskPunttMessage): boolean {
  // Only ever called on a row `mine()` has already cleared, so the overlay's own
  // row is gone by here — one guard, in one place.
  return message.role === "assistant" && message.status === "pending";
}

/**
 * The turn a marker row belongs to: the OLDEST person's message in the
 * UNANSWERED run above it, not simply the nearest one.
 *
 * The nearest one is wrong as soon as two turns are outstanding, and that is a
 * shape both readers are handed. A message sent while a review runs gets its own
 * in-flight assistant row immediately — `assistantFromAck`
 * (`createAskV2Transport.ts`) marks every ACK that omits `assistantMessage`
 * `streaming`, which the live create/send ACK always does, and the overlaid list
 * carries that row too — so the thread reads
 * `[turn, marker, follow-up, follow-up's own marker]`. Walking one step up from
 * the tail marker names the FOLLOW-UP, whose turn warhol has not started: the
 * socket arms on an address nobody publishes to, and the turn that IS running
 * goes dark until the next history poll. Sending two messages before any marker
 * of the first survives a sync collapses that to `[turn, follow-up, marker]`,
 * where the nearest user row is the follow-up again.
 *
 * So the walk passes user rows and other markers alike and keeps the LAST one it
 * sees, stopping at the first SETTLED answer — a turn with an answer is finished,
 * and nothing above that answer is outstanding. Warhol answers a chat's turns in
 * order, so the oldest outstanding turn is the one running.
 *
 * Two shapes the walk has to step over rather than stop on, and one it must stop
 * on, are all reachable from the same list:
 *
 * - a row carrying no `status` is a turn's own plan or tool row (`planMessage` in
 *   `useAskV2PlanProgress.test.ts` — an assistant row with `agentSteps` and no
 *   status), not an answer. Stopping there turns a running turn into "finished"
 *   whenever the turn has written a plan, and every plan row draws `done` at
 *   once — the complaint this turn scoping exists to answer.
 * - a marker above the one we started from belongs to the same outstanding run
 *   (the follow-up's own in-flight row), so it is passed too.
 *
 * But a no-status row is only stepped over while NO person's message has been
 * taken yet. Once one has, a non-marker above it bounds the run: two turns have
 * been asked and the older one is settled, which is `u2` in a
 * `[u1, plan, u2, marker]` list — reaching past it to `u1` would put the
 * previous turn's finished plan on the rail for the turn that is running.
 */
function turnAbove<T extends { role: string; status?: unknown }>(
  rows: readonly (T | undefined)[],
  markerIndex: number,
  mine: (row: T | undefined) => boolean,
  isMarker: (row: T) => boolean,
): T | null {
  let turn: T | null = null;
  for (let above = markerIndex - 1; above >= 0; above -= 1) {
    const candidate = rows[above];
    if (!mine(candidate)) continue;
    if (candidate!.role === "user") {
      turn = candidate!;
      continue;
    }
    if (isMarker(candidate!)) continue;
    if (turn == null && candidate!.status == null) continue;
    break;
  }
  return turn;
}

/**
 * The message the RUNNING warhol turn belongs to, or null when none is.
 *
 * One rule, in one place, because three readers have to name the same turn:
 *
 * - `streamMessageIdFor` builds the snapshot address from it;
 * - the rail stamps that turn's review onto its live findings from it
 *   (`collectRailContent.ts::collectLiveFindings`);
 * - christo resolves the same message server-side
 *   (`warholChat.ts::warholTurnUserMessageId`) to write the provisional review.
 *
 * A `pending` assistant row is christo's placeholder for the running turn
 * (`upsertWarholTurnPlaceholder` — it appears as soon as the turn has a tool
 * call to show, which on a real review is minutes before the first finding).
 * Two things follow, and both matter:
 *
 * - The placeholder is the turn's MARKER, so the turn's message is the person's
 *   own above it. That is the case a follow-up breaks: a second message sent
 *   while the review ran sits BELOW the placeholder, is the last user row, and
 *   belongs to a turn warhol has not started. Reading the last user row would
 *   name an address nobody publishes to — and, for the rail, would find no
 *   review yet — for the whole of the turn that IS running.
 * - With no placeholder the turn's first polls are the only case left, and the
 *   last message being the person's is what says the answer has not landed.
 *
 * christo resolves the same thing from the one signal this side does not have:
 * the placeholder's turn identity. Its row carries
 * `ingressKey = warhol-turn:<session>:<askedAt>`, and the serializer drops
 * `ingressKey` from every assistant row the browser is served
 * (`askPunttChat.ts`, which passes it through for user messages only). So the
 * browser identifies the marker by SHAPE — the newest `pending` assistant row —
 * where the server identifies it by key.
 *
 * The two agree because the server keeps the SHAPE true, not because the
 * predicates are identical. `upsertWarholTurnPlaceholder` only ever creates
 * this row when the transcript ends with the person's message, and
 * `warholStalePlaceholderKeys` prunes every placeholder that is no longer the
 * running turn's on the same read that builds the response — so a served thread
 * carries at most one `pending` row, and it is the running turn's. That prune,
 * one service away, is what makes "newest pending row" this side's marker;
 * without it a stranded placeholder above a newer question would name the
 * previous turn here and the current one on the server.
 *
 * That prune governs the SERVED thread, and the list this reads is not always
 * that: `useAskV2Transport` overlays the local thread onto the payload before
 * the rail sees it, which adds the local follow-up's own in-flight row as a
 * second `pending` marker. So the marker names its turn through `turnAbove`,
 * which resolves the oldest turn still outstanding above it rather than the
 * nearest user row.
 *
 * Narrowing it further is not open to us: the key that would settle it is the
 * field the serializer strips. Rejecting the marker whenever a newer user row
 * sits below it is worse than approximating — that case is a follow-up queued
 * mid-review, and it would name the follow-up's address, which nobody has
 * published to yet, for the whole of the turn that IS running.
 */
export function runningTurnUserMessageId(
  messages: readonly AskPunttMessage[] | undefined,
): string | null {
  const rows = messages ?? [];
  // The overlay's own row is not christo's, so it is neither the marker nor the
  // answer the turn has landed: it is the stream's live picture of a turn that
  // is still running, and the turn's message is above it like any other.
  const mine = (message: AskPunttMessage | undefined) =>
    !!message && !message.hidden && !isSynthesizedRow(message);

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const message = rows[index];
    if (!mine(message) || !isPendingRow(message!)) continue;
    // Not the nearest user row above the marker — the oldest one still
    // outstanding. The list the rail reads is the OVERLAID one, so the local
    // follow-up's own in-flight row arrives as a second `pending` marker below
    // this turn's; see `turnAbove`.
    return turnAbove(rows, index, mine, isPendingRow)?._id ?? null;
  }

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const message = rows[index];
    if (!mine(message)) continue;
    return message!.role === "user" ? message!._id : null;
  }
  return null;
}

/**
 * The running turn's message id, read off the LOCAL thread instead of history.
 *
 * `runningTurnUserMessageId` reads the server's rows, which requires a history
 * payload. This reads the transport's own rows, so the socket can be armed for
 * a turn this tab just started without waiting for a read that may be failing —
 * and the review is then drawn off the live stream instead of going dark for
 * the whole of a turn the server is running.
 *
 * Same rule, same two cases, so both name one turn:
 *
 * - christo's placeholder is the turn's MARKER, and `mapAssistantStatus` maps
 *   its `pending` status to `streaming` here, so the person's own message above
 *   it is the turn's.
 * - with no placeholder written yet — the normal first minutes of a warhol
 *   review — the person's message being the tail is what says the answer has
 *   not landed.
 *
 * A row with no `serverId` has not been acknowledged, so it names no address a
 * snapshot could be filed under: this returns null rather than a client uuid no
 * producer would ever write. `sending`, `failed` and `aborted` rows are in that
 * class, and they are terminal — a failed send must not hold a subscription
 * open for a turn the server never started.
 */
export function runningTurnUserMessageIdFromThread(
  messages: readonly AskV2ThreadMessage[] | undefined,
): string | null {
  const rows = messages ?? [];
  // The overlay's own row is keyed by the snapshot address, not by a stored
  // message id, so it is neither the marker nor the answer — the turn's message
  // is above it, exactly as in `runningTurnUserMessageId`.
  const mine = (message: AskV2ThreadMessage | undefined) =>
    !!message &&
    !isStreamSnapshotMessageId(message.serverId) &&
    !isStreamSnapshotMessageId(message.clientId);

  // The placeholder is looked for over the WHOLE list, not just the tail, and
  // the turn is the oldest one still outstanding above it, not the nearest user
  // row: a follow-up queued behind the running turn sits BELOW the marker AND
  // brings a streaming ACK row of its own (`assistantFromAck`), so the nearest
  // user row above the tail marker is the follow-up — an address nobody has
  // published to yet, for the whole of the turn that IS running. Same rule,
  // same reason as `runningTurnUserMessageId`; see `turnAbove`.
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const message = rows[index];
    if (!mine(message) || !isStreamingRow(message!)) continue;
    return turnAbove(rows, index, mine, isStreamingRow)?.serverId ?? null;
  }

  // No marker: the answer has not landed iff the person's own message is the
  // tail. `sending` has no server id yet and `failed`/`aborted` are terminal,
  // and neither names a turn the server is running.
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const message = rows[index];
    if (!mine(message)) continue;
    if (message!.role !== "user" || message!.status !== "sent") return null;
    return message!.serverId ?? null;
  }
  return null;
}

/** Whether a row is christo's placeholder for a turn that is still running. */
function isStreamingRow(message: AskV2ThreadMessage): boolean {
  // `mapAssistantStatus` maps the placeholder's server `pending` status onto
  // this one, so the local thread carries the same marker the server rows do.
  return message.role === "assistant" && message.status === "streaming";
}

/**
 * The review the RUNNING turn is being written into, or undefined.
 *
 * Scoped to the running turn on purpose: `viewReviewId` appears on a message row
 * only once christo's provisional write has run and a later history poll has
 * returned it, so between warhol publishing the first finding and that poll
 * landing the newest row carrying one is the PREVIOUS turn's settled answer.
 * Taking that would render an openable row that opens a different review.
 *
 * The row it wants is the assistant row that FOLLOWS the running turn's message
 * — the placeholder christo wrote for it, which is the same row the review id is
 * stamped on. Nothing newer: a later turn's answer is a different turn's.
 */
export function runningTurnReviewId(
  messages: readonly AskPunttMessage[] | undefined,
): string | undefined {
  const rows = messages ?? [];
  const userId = runningTurnUserMessageId(rows);
  if (!userId) return undefined;
  const from = rows.findIndex((row) => row?._id === userId);
  if (from < 0) return undefined;
  for (let index = from + 1; index < rows.length; index += 1) {
    const message = rows[index];
    if (!message || message.hidden || isSynthesizedRow(message)) continue;
    if (message.role === "user") return undefined;
    if (message.viewReviewId) return message.viewReviewId;
  }
  return undefined;
}
